import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertContext, type AlertContextValue } from '../alert.context.ts'
import type { Alert, AlertOptions } from '@/types/alert.types.ts'

const DEFAULT_DURATION = 5000
const MAX_ALERTS = 5

interface AlertProviderProps {
  children: ReactNode
  maxAlerts?: number
}

export function AlertProvider({
  children,
  maxAlerts = MAX_ALERTS,
}: AlertProviderProps) {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  )

  const dismissAlert = useCallback((id: string) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setAlerts((prev) => prev.filter((alert) => alert.id !== id))
  }, [])

  const showAlert = useCallback(
    (options: AlertOptions) => {
      const id = `alert-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const duration = options.duration ?? DEFAULT_DURATION
      const dismissible = options.dismissible ?? true

      const alert: Alert = {
        id,
        type: options.type,
        message: options.message,
        duration,
        dismissible,
      }

      setAlerts((prev) => {
        const next = [...prev, alert]
        return next.length > maxAlerts ? next.slice(-maxAlerts) : next
      })

      if (duration > 0) {
        const timer = setTimeout(() => {
          dismissAlert(id)
        }, duration)
        timersRef.current.set(id, timer)
      }
    },
    [dismissAlert, maxAlerts],
  )

  const clearAlerts = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer))
    timersRef.current.clear()
    setAlerts([])
  }, [])

  const value: AlertContextValue = useMemo(
    () => ({
      alerts,
      showAlert,
      dismissAlert,
      clearAlerts,
    }),
    [alerts, showAlert, dismissAlert, clearAlerts],
  )

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}
